// O klientce (schválený mockup 8A a 8B, 1. 11. 2026): krátký přehled klientky pro sales
// před hovorem. Tlačítko O klientce v Hodnocení výživy a v Bývalých klientkách, detail
// jako okno (na počítači uprostřed, na mobilu panel zespodu, Modal z ui.jsx) a stejný
// obsah jako rozbalovací blok v obrazovce hovoru.
// Data: GET /api/hodnoceni-vyzivy?akce=o_klientce (server pustí jen owner, CSM a sales).
// Server vrací jen pole pro hovor: žádné platby, e-maily, zdravotní údaje ani poznámky.
import React, { useEffect, useRef, useState } from "react";
import { Info, Phone, User, ChevronDown } from "lucide-react";
import { Modal, HlavickaOkna, SkeletonRows, ErrorNote } from "../ui.jsx";
import { Pil } from "./salesUi.jsx";
import { hvGet, datumKratce, nazevMesice, krestni } from "../../lib/hodnoceniVyzivy.js";
import { getRealUser } from "../../lib/auth.js";

/** Role, kterým server detail pustí (manažer v náhledu Sales ne). */
export function smiOKlientce() {
  const r = String(getRealUser()?.role || "").toLowerCase();
  return r === "owner" || r === "csm" || r === "sales";
}

/** Martina -> Martinou (7. pád ženských jmen na -a), jinak beze změny. */
function sKym(jmeno) { const k = krestni(jmeno); return /a$/i.test(k) ? `${k.slice(0, -1)}ou` : k; }

function tonStavu(stav) {
  const s = String(stav || "").toLowerCase();
  if (s.startsWith("aktiv")) return "ok";
  if (s.startsWith("přeruš") || s.startsWith("preru")) return "pozor";
  return "neutralni";
}

/** Načtení detailu jedné klientky. */
function useOKlientce(klientId) {
  const [d, setD] = useState(null);
  const [chyba, setChyba] = useState("");
  const [pokus, setPokus] = useState(0);
  useEffect(() => {
    if (!klientId) return undefined;
    let zije = true;
    setChyba("");
    hvGet("o_klientce", { klientId })
      .then((j) => { if (zije) setD(j.klientka || null); })
      .catch((e) => { if (zije) setChyba(e.message); });
    return () => { zije = false; };
  }, [klientId, pokus]);
  return { d, chyba, znovu: () => setPokus((n) => n + 1) };
}

/** Tlačítko O klientce (mockup 8A). Nespouští klik na řádek pod sebou. */
export function OKlientceTlacitko({ onClick, className = "" }) {
  return (
    <button type="button" onClick={(e) => { e.stopPropagation(); onClick?.(e); }}
      className={`btn-secondary min-h-[48px] justify-center whitespace-nowrap px-4 ${className}`}>
      <Info className="h-5 w-5" />O klientce
    </button>
  );
}

function Radek({ popis, children }) {
  return (
    <div className="flex items-start justify-between gap-3 px-3 py-2.5 sm:px-4">
      <span className="shrink-0 text-[14px] text-slate-500">{popis}</span>
      <span className="min-w-0 text-right text-[14px] font-semibold text-slate-900 [overflow-wrap:anywhere]">{children}</span>
    </div>
  );
}

/** Obsah detailu (okno i rozbalovací blok v hovoru). */
export function OKlientceObsah({ d }) {
  const n = d.nutri || {};
  const nutriText = n.pocet
    ? `${n.pocet}× (poslední ${datumKratce(n.posledni)}${n.nutricni ? ` s ${sKym(n.nutricni)}` : ""})`
    : "zatím žádná";
  return (
    <div className="space-y-3">
      <div className="flex flex-wrap gap-1.5">
        {d.stav && <Pil ton={tonStavu(d.stav)} casti={d.stav} />}
        {d.balicek && <Pil ton="znacka" casti={d.balicek} />}
        {d.lokalita && <Pil casti={d.lokalita} />}
      </div>
      <div className="divide-y divide-slate-100 rounded-2xl border border-slate-200 bg-white">
        <Radek popis="Trenér">{d.trener || "neuvedeno"}</Radek>
        <Radek popis="S námi od">{d.zacatek ? `${datumKratce(d.zacatek, true)}${d.delka ? `, ${d.delka}` : ""}` : "neuvedeno"}</Radek>
        <Radek popis="Poslední trénink">{d.posledni_trenink ? datumKratce(d.posledni_trenink, true) : "zatím žádný"}</Radek>
        <Radek popis="Tréninky">{d.treninku_tydne ? `${d.treninku_tydne}× týdně` : "podle balíčku"}</Radek>
        <Radek popis="Nutriční konzultace">{nutriText}</Radek>
      </div>
      <div className="space-y-2 rounded-2xl bg-brand-50 p-3 sm:p-4">
        <div className="text-[12px] font-bold uppercase tracking-wider text-brand-deep">Minulá hodnocení výživy</div>
        {(d.hodnoceni || []).length ? (
          <div className="flex flex-wrap gap-1.5">
            {d.hodnoceni.map((h) => (
              <span key={h.mesic} className="inline-flex items-center gap-1.5 whitespace-nowrap rounded-full bg-white px-3 py-1 text-[13px] font-bold text-amber-700 shadow-sm"
                aria-label={`${nazevMesice(h.mesic)}, ${h.hvezdy} z 5 hvězd`}>
                {nazevMesice(h.mesic).toLowerCase()}<span aria-hidden className="tracking-tight">{"★".repeat(Math.max(0, Math.min(5, h.hvezdy)))}</span>
              </span>
            ))}
          </div>
        ) : (
          <div className="text-[13px] text-slate-600">Zatím žádné.</div>
        )}
      </div>
      <p className="px-1 text-[12px] text-slate-500">Bez plateb, kontaktů navíc a zdravotních údajů.</p>
    </div>
  );
}

/** Okno O klientce (mockup 8B). onZavolat (volitelné) otevře obrazovku hovoru, bez něj je okno bez patičky. */
export function OKlientceOkno({ klientId, jmeno, onZavolat, onClose }) {
  const { d, chyba, znovu } = useOKlientce(klientId);
  return (
    <Modal open onClose={onClose} plnaHlavicka size="md"
      header={<HlavickaOkna ikona={User} nazev={d?.klient_name || jmeno || "Klientka"} popis="O klientce, jen pro hovor" onClose={onClose} />}
      footer={onZavolat ? (
        <div className="flex justify-end">
          <button type="button" onClick={onZavolat} className="btn-primary min-h-[48px] w-full justify-center px-5 sm:w-auto">
            <Phone className="h-5 w-5" />Zavolat
          </button>
        </div>
      ) : undefined}>
      {chyba ? <ErrorNote onRetry={znovu}>{chyba}</ErrorNote> : !d ? <SkeletonRows rows={4} /> : <OKlientceObsah d={d} />}
    </Modal>
  );
}

/** Rozbalovací blok O klientce v obrazovce hovoru. Načte se až po rozbalení. */
export function OKlientceRozbalovaci({ klientId }) {
  const [otevreno, setOtevreno] = useState(false);
  const obsahRef = useRef(null);
  useEffect(() => {
    if (otevreno) obsahRef.current?.scrollIntoView({ behavior: "smooth", block: "nearest" });
  }, [otevreno]);
  return (
    <div className="rounded-2xl border border-slate-200 bg-white">
      <button type="button" onClick={() => setOtevreno((o) => !o)} aria-expanded={otevreno}
        className="flex min-h-[48px] w-full items-center gap-2 px-3 text-left sm:px-4">
        <Info className="h-4 w-4 shrink-0 text-brand-deep" />
        <span className="flex-1 text-[14px] font-bold text-slate-900">O klientce</span>
        <ChevronDown className={`h-4 w-4 text-slate-400 transition-transform ${otevreno ? "" : "-rotate-90"}`} />
      </button>
      {otevreno && (
        <div ref={obsahRef} className="scroll-mt-4 border-t border-slate-100 p-3 sm:p-4">
          <RozbalenyObsah klientId={klientId} />
        </div>
      )}
    </div>
  );
}
function RozbalenyObsah({ klientId }) {
  const { d, chyba, znovu } = useOKlientce(klientId);
  if (chyba) return <ErrorNote onRetry={znovu}>{chyba}</ErrorNote>;
  if (!d) return <SkeletonRows rows={3} />;
  return <OKlientceObsah d={d} />;
}
